<template>
  <div
    class="ai-chat"
    :class="{ 'is-compact': compact, 'is-docked': docked }"
  >
    <div class="ai-toolbar">
      <el-button
        type="primary"
        :disabled="streaming"
        @click="startNewConversation"
      >
        <MdiIcon
          name="PlusCircle"
          :size="18"
        />
        Nouvelle conversation
      </el-button>
    </div>

    <!-- En panneau, la largeur ne permet pas deux colonnes : le fil et
         l'historique se partagent la place en onglets. Les volets d'`el-tabs`
         restent vides, ils ne servent qu'à en dessiner les en-têtes — le
         contenu, lui, garde sa place dans la grille ci-dessous. -->
    <el-tabs
      v-if="docked"
      v-model="dockedView"
      class="ai-docked-tabs"
    >
      <el-tab-pane
        label="Conversation"
        name="chat"
      />
      <el-tab-pane
        :label="conversations.length ? `Historique (${conversations.length})` : 'Historique'"
        name="history"
      />
    </el-tabs>

    <div class="ai-layout">
      <!-- Historique -->
      <el-card
        v-show="!docked || dockedView === 'history'"
        shadow="never"
        class="ai-sidebar"
        body-class="ai-sidebar-body"
      >
        <div
          v-loading="loadingConversations"
          class="ai-conversation-list"
        >
          <el-empty
            v-if="!loadingConversations && conversations.length === 0"
            description="Aucune conversation"
            :image-size="70"
          />

          <button
            v-for="conversation in conversations"
            :key="conversation.id"
            type="button"
            class="ai-conversation-item"
            :class="{ 'is-active': conversation.id === currentId }"
            @click="openConversation(conversation.id)"
          >
            <span class="ai-conversation-title">{{ conversation.title || 'Nouvelle conversation' }}</span>
            <span class="ai-conversation-meta">{{ formatDate(conversation.last_message_at || conversation.created_at) }}</span>
            <el-popconfirm
              title="Supprimer cette conversation ?"
              confirm-button-text="Supprimer"
              cancel-button-text="Annuler"
              confirm-button-type="danger"
              @confirm="removeConversation(conversation.id)"
            >
              <template #reference>
                <span
                  class="ai-conversation-delete"
                  @click.stop
                >
                  <MdiIcon
                    name="TrashCanOutline"
                    :size="15"
                  />
                </span>
              </template>
            </el-popconfirm>
          </button>
        </div>
      </el-card>

      <!-- Fil de discussion -->
      <el-card
        v-show="!docked || dockedView === 'chat'"
        shadow="never"
        class="ai-main"
        body-class="ai-main-body"
      >
        <div
          ref="threadRef"
          class="ai-thread"
        >
          <div
            v-if="messages.length === 0 && !streaming"
            class="ai-placeholder"
          >
            <LuceMark
              :size="56"
              :animated="false"
            />
            <p class="ai-placeholder-titre">
              Luce, l'assistante interne de Prosoluce
            </p>
            <p>
              Elle rédige et reformule vos courriers et messages, résume, traduit,
              explique, relit du code, prépare un modèle de document, et sait vous
              dire dans quel écran de gestion-v2 aller.
            </p>
            <p class="ai-placeholder-reserve">
              Elle n'a accès à aucune donnée de gestion-v2 : ni client, ni facture,
              ni contrat. Les échanges sont enregistrés dans votre historique,
              visible de vous seul.
            </p>
          </div>

          <div
            v-for="message in messages"
            :key="message.key"
            class="ai-message"
            :class="`is-${message.role}`"
          >
            <div class="ai-message-bubble">
              <!-- Le document n'est pas conservé : son étiquette reste, pour
                   qu'on comprenne en relisant le fil sur quoi portait la
                   question. -->
              <div
                v-if="message.attachment"
                class="ai-message-attachment"
              >
                <MdiIcon
                  name="FileDocumentOutline"
                  :size="14"
                />
                {{ message.attachment.name }}
                <span
                  v-if="message.attachment.truncated"
                  class="ai-attachment-meta"
                >· document raccourci</span>
              </div>
              <AiMessageContent
                :content="message.content"
                :pending="message.pending === true"
              />
              <div
                v-if="message.failed"
                class="ai-message-error"
              >
                <MdiIcon
                  name="AlertCircleOutline"
                  :size="15"
                />
                {{ message.error || 'La génération a échoué.' }}
              </div>
            </div>
            <div
              v-if="message.role === 'assistant' && message.model"
              class="ai-message-footer"
            >
              <!-- Téléchargement d'une réponse terminée : le document est
                   produit côté serveur à partir du contenu enregistré. -->
              <el-dropdown
                v-if="message.id && !message.pending && !message.failed"
                trigger="click"
                class="ai-message-download"
                @command="(format) => download(message, format)"
              >
                <span class="ai-download-trigger">
                  <MdiIcon
                    name="TrayArrowDown"
                    :size="14"
                  />
                  Télécharger
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item
                      v-for="f in exportFormats"
                      :key="f.format"
                      :command="f.format"
                    >
                      {{ f.label }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              {{ labelForModel(message.model) }}
              <!-- Espace avant le séparateur voulu : pas de saut de ligne ici. -->
              <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
              <template v-if="message.duration_ms"> · {{ formatDuration(message.duration_ms) }}</template>
            </div>
          </div>
        </div>

        <!-- Le dépôt se fait sur le composer lui-même : dans un fil de
             conversation, une zone de dépôt séparée n'aurait nulle part où
             tenir sans pousser la saisie hors de l'écran. -->
        <div
          class="ai-composer"
          :class="{ 'is-dropping': dropping }"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
        >
          <div
            v-if="attachment || uploading"
            class="ai-attachment"
          >
            <MdiIcon
              :name="attachmentIcon"
              :size="16"
            />
            <span
              v-if="uploading"
              class="ai-attachment-name"
            >{{ uploadingAudio ? 'Transcription de l’enregistrement…' : 'Lecture du document…' }}</span>
            <template v-else>
              <span class="ai-attachment-name">{{ attachment.name }}</span>
              <span class="ai-attachment-meta">
                {{ formatChars(attachment.chars) }} {{ attachment.transcribed ? 'transcrits' : 'lus' }}
                <template v-if="attachment.truncated"> · {{ attachment.transcribed ? "transcription raccourcie" : "document raccourci" }}</template>
              </span>
              <el-button
                text
                size="small"
                :disabled="streaming"
                @click="clearAttachment"
              >
                <MdiIcon
                  name="Close"
                  :size="14"
                />
              </el-button>
            </template>
          </div>

          <el-input
            v-model="draft"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 10 }"
            resize="none"
            :maxlength="MAX_INPUT_CHARS"
            show-word-limit
            :placeholder="composerPlaceholder"
            :disabled="streaming"
            @keydown.enter.exact.prevent="send"
            @paste="onPaste"
          />
          <div class="ai-composer-actions">
            <!-- Fallback clavier du glisser-déposer, et seul chemin possible
                 depuis un explorateur de fichiers. -->
            <el-button
              v-if="attachmentsEnabled"
              text
              :disabled="streaming || uploading"
              :title="attachmentHint"
              @click="pickFile"
            >
              <MdiIcon
                name="Paperclip"
                :size="18"
              />
              Joindre
            </el-button>
            <input
              ref="fileInputRef"
              type="file"
              class="ai-file-input"
              :accept="acceptAttribute"
              @change="onFilePicked"
            >
            <el-button
              v-if="streaming"
              @click="stopGeneration"
            >
              <MdiIcon
                name="StopCircleOutline"
                :size="18"
              />
              Arrêter
            </el-button>
            <el-button
              v-else
              type="primary"
              :disabled="!canSend"
              @click="send"
            >
              <MdiIcon
                name="Send"
                :size="18"
              />
              Envoyer
            </el-button>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import AiMessageContent from './AiMessageContent.vue';
import LuceMark from '@/components/Luce/LuceMark.vue';
import {
  createConversation,
  downloadMessage,
  fetchExportFormats,
  deleteConversation,
  fetchConversation,
  fetchConversations,
  fetchModels,
  streamMessage,
  uploadAttachment,
} from '@/api/aiChat';
import { useSmoothStream } from '@/composables/useSmoothStream';

/**
 * Luce — le chat lui-même, sans décor.
 *
 * Le même composant sert la page dédiée et la fenêtre flottante ; seule la
 * hauteur change, via `compact`. Rien ne doit donc dépendre de la route.
 *
 * La conversation n'est créée en base qu'à l'envoi du premier message : cliquer
 * sur « Nouvelle conversation » ne laisse pas de coquille vide derrière soi.
 */

defineProps({
  /** Format fenêtre : hauteur réduite et colonne d'historique resserrée. */
  compact: { type: Boolean, default: false },
  // Panneau latéral : la largeur disponible ne permet plus deux colonnes.
  docked: { type: Boolean, default: false },
});

// Onglet courant du panneau. Sans effet hors panneau, où les deux volets sont
// affichés côte à côte.
const dockedView = ref('chat');

const MAX_INPUT_CHARS = 16000;

// Le catalogue n'est plus un sélecteur : il ne sert qu'à nommer, sous une
// réponse, le modèle qui l'a produite. Le choix appartient au serveur.
const models = ref([]);
const conversations = ref([]);
const messages = ref([]);
const currentId = ref(null);
const draft = ref('');
const streaming = ref(false);
const loadingConversations = ref(false);
const threadRef = ref(null);

/**
 * Tant que le lecteur n'a pas remonté le fil, l'affichage reste collé en bas.
 * Dès qu'il remonte, on cesse de le suivre : une réponse qui s'écrit ne doit
 * pas lui arracher sa lecture des yeux.
 */
let stickToBottom = true;

/** Marge sous laquelle on considère qu'on est encore « en bas » du fil. */
const STICK_THRESHOLD_PX = 120;

// Lissage de l'affichage des réponses : les fragments reçus sont écrits à
// débit régulier plutôt qu'au rythme irrégulier du serveur d'inférence.
const smoothStream = useSmoothStream();
const exportFormats = ref([]);

// Pièce jointe en attente d'envoi : le serveur en a déjà extrait le texte, il
// ne reste ici que sa fiche. Une seule à la fois — deux documents dans un même
// message, c'est presque toujours une erreur de manipulation.
const attachment = ref(null);
const uploading = ref(false);
const downloading = ref(false);
const dropping = ref(false);
const fileInputRef = ref(null);
const attachmentRules = ref({
  enabled: false,
  formats: [],
  max_bytes: 0,
  audio_formats: [],
  audio_max_bytes: 0,
});

let dragDepth = 0;

const attachmentsEnabled = computed(
  () => attachmentRules.value.enabled && attachmentRules.value.formats.length > 0,
);

const acceptAttribute = computed(
  () => attachmentRules.value.formats.map((f) => `.${f}`).join(','),
);

const attachmentHint = computed(
  () => `Formats acceptés : ${attachmentRules.value.formats.join(', ').toUpperCase()}`
    + ` ; ${Math.round(attachmentRules.value.max_bytes / 1048576)} Mo maximum`
    + (attachmentRules.value.audio_formats.length
      ? `, ${Math.round(attachmentRules.value.audio_max_bytes / 1048576)} Mo pour un enregistrement.`
      : '.'),
);

/**
 * Un enregistrement pèse pour sa durée : il a son propre plafond, le même que
 * celui appliqué côté serveur.
 */
function limitePour(extension) {
  return attachmentRules.value.audio_formats.includes(extension)
    ? attachmentRules.value.audio_max_bytes
    : attachmentRules.value.max_bytes;
}

// Transcrire prend plus longtemps que lire : l'attente mérite d'être nommée.
const uploadingAudio = ref(false);

const attachmentIcon = computed(() => {
  if (uploading.value) return 'ProgressClock';

  return attachment.value?.transcribed ? 'Microphone' : 'FileDocumentOutline';
});

const composerPlaceholder = computed(() => (attachmentsEnabled.value
  ? 'Écris ton message, ou dépose un document. Entrée pour envoyer, Maj+Entrée pour aller à la ligne'
  : 'Écris ton message. Entrée pour envoyer, Maj+Entrée pour aller à la ligne'));

// Un document déposé sans un mot est une demande en soi : le bouton s'active.
const canSend = computed(
  () => !uploading.value && (draft.value.trim() !== '' || attachment.value !== null),
);

let abortController = null;
let messageKey = 0;

const nextKey = () => `m-${++messageKey}`;

onMounted(async () => {
  await Promise.all([loadModels(), loadConversations(), loadExportFormats()]);
});

onBeforeUnmount(() => {
  if (abortController) abortController.abort();
});

async function loadModels() {
  try {
    const { data, meta } = await fetchModels();
    models.value = data || [];
    // Formats et poids viennent du serveur : un fichier hors clous est écarté
    // sans faire l'aller-retour, avec les mêmes règles des deux côtés.
    // Fusion et non remplacement : un backend plus ancien ne renvoie pas les
    // clés récentes, et les écraser laisserait des `undefined` que les
    // computed consommés par le template feraient exploser au rendu — écran
    // de chat blanc pour une simple avance du bundle sur l'API.
    attachmentRules.value = { ...attachmentRules.value, ...(meta?.attachments ?? {}) };
  } catch (e) {
    // L'appel porte aussi les règles de pièce jointe : son échec n'est donc
    // pas muet, même sans sélecteur à peupler.
    ElMessage.error('Impossible de charger les réglages de la conversation.');
  }
}

async function loadExportFormats() {
  try {
    const { data } = await fetchExportFormats();
    exportFormats.value = data || [];
  } catch (e) {
    // Sans la liste, le bouton ne s'affiche pas : le chat reste utilisable.
    exportFormats.value = [];
  }
}

/** Produit et télécharge le document ; le contenu vient du serveur, pas de l'écran. */
async function download(message, format) {
  // Deux clics sur un format lent, ce sont deux synthèses de vingt-cinq
  // secondes côté proxy pour un seul fichier voulu.
  if (downloading.value) return;

  // Produire un PDF est instantané, lire une réponse à voix haute prend une
  // vingtaine de secondes : sans rien à l'écran, le clic paraît perdu et on
  // recommence. Le serveur dit quels formats sont lents — les reconnaître
  // ici par leur nom ferait disparaître l'attente au prochain format ajouté.
  const lent = exportFormats.value.find((f) => f.format === format)?.slow === true;

  downloading.value = true;
  const attente = lent
    ? ElMessage({
      message: 'Lecture en cours de préparation…',
      type: 'info',
      duration: 0,
      showClose: true,
    })
    : null;

  try {
    await downloadMessage(currentId.value, message.id, format);
  } catch (e) {
    ElMessage.error(e.message);
  } finally {
    attente?.close();
    downloading.value = false;
  }
}

async function loadConversations() {
  loadingConversations.value = true;
  try {
    const { data } = await fetchConversations({ per_page: 50 });
    conversations.value = data || [];
  } catch (e) {
    ElMessage.error("Impossible de charger l'historique.");
  } finally {
    loadingConversations.value = false;
  }
}

async function openConversation(id) {
  // Choisir une conversation dans l'historique, c'est vouloir la lire : en
  // panneau, on revient au fil sans avoir à changer d'onglet à la main.
  dockedView.value = 'chat';

  if (streaming.value || id === currentId.value) return;

  try {
    const { data } = await fetchConversation(id);
    currentId.value = data.id;
    messages.value = (data.messages || []).map((m) => ({ ...m, key: nextKey() }));
    scrollToBottom(true);
  } catch (e) {
    ElMessage.error('Cette conversation est introuvable.');
    await loadConversations();
  }
}

function startNewConversation() {
  dockedView.value = 'chat';
  currentId.value = null;
  messages.value = [];
  draft.value = '';
  clearAttachment();
}

/**
 * Ouvre la conversation si elle n'existe pas encore.
 *
 * Le dépôt d'un document en a besoin autant que l'envoi d'un message : les
 * deux routes sont portées par une conversation.
 */
async function ensureConversation() {
  if (currentId.value) return true;

  try {
    // Sans `model` : le serveur applique le sien. Envoyer une valeur vide
    // serait refusé par la validation, qui n'accepte qu'un modèle du catalogue.
    const { data } = await createConversation();
    currentId.value = data.id;
    conversations.value.unshift(data);

    return true;
  } catch (e) {
    ElMessage.error("La conversation n'a pas pu être ouverte.");

    return false;
  }
}

function pickFile() {
  fileInputRef.value?.click();
}

function onFilePicked(event) {
  const [file] = event.target.files || [];

  // Remis à zéro sans attendre : sans ça, redéposer le même fichier après un
  // refus ne déclenche aucun évènement.
  event.target.value = '';

  if (file) acceptFile(file);
}

/**
 * Le compteur compense le comportement de `dragleave`, qui se déclenche aussi
 * en passant d'un élément enfant à un autre : sans lui, la zone clignote.
 */
function onDragEnter(event) {
  if (!attachmentsEnabled.value || streaming.value) return;
  if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;

  dragDepth += 1;
  dropping.value = true;
}

function onDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);

  if (dragDepth === 0) dropping.value = false;
}

function onDrop(event) {
  dragDepth = 0;
  dropping.value = false;

  if (!attachmentsEnabled.value || streaming.value) return;

  const [file] = event.dataTransfer?.files || [];

  if (file) acceptFile(file);
}

/** Coller un document depuis l'explorateur, plutôt que le glisser. */
function onPaste(event) {
  if (!attachmentsEnabled.value || streaming.value) return;

  const [file] = event.clipboardData?.files || [];

  if (!file) return;

  event.preventDefault();
  acceptFile(file);
}

/**
 * Contrôle ce qui peut l'être ici, puis confie le fichier au serveur — lui
 * seul peut dire si le contenu est réellement lisible.
 */
async function acceptFile(file) {
  // Transcrire prend des dizaines de secondes : sans cette garde, un second
  // dépôt pendant l'attente laisse deux envois en vol, et c'est celui qui
  // répond en dernier qui reste joint — pas celui qu'on croit.
  if (uploading.value) {
    ElMessage.warning('Un fichier est déjà en cours de lecture.');
    return;
  }

  const extension = file.name.split('.').pop()?.toLowerCase() || '';

  if (!attachmentRules.value.formats.includes(extension)) {
    ElMessage.warning(attachmentHint.value);
    return;
  }

  const limite = limitePour(extension);

  if (file.size > limite) {
    ElMessage.warning(
      `Ce fichier fait ${formatSize(file.size)}, la limite est de `
      + `${Math.round(limite / 1048576)} Mo.`,
    );
    return;
  }

  if (!(await ensureConversation())) return;

  uploading.value = true;
  uploadingAudio.value = attachmentRules.value.audio_formats.includes(extension);

  try {
    const fiche = await uploadAttachment(currentId.value, file);

    if (fiche) attachment.value = fiche;
  } catch (e) {
    ElMessage.error(e.message);
  } finally {
    uploading.value = false;
    uploadingAudio.value = false;
  }
}

function clearAttachment() {
  attachment.value = null;
  uploading.value = false;
}


async function removeConversation(id) {
  try {
    await deleteConversation(id);
    conversations.value = conversations.value.filter((c) => c.id !== id);

    if (currentId.value === id) startNewConversation();
  } catch (e) {
    ElMessage.error('La suppression a échoué.');
  }
}

async function send() {
  const content = draft.value.trim();
  const jointe = attachment.value;

  if (!canSend.value || streaming.value) return;

  // Création à la volée : la conversation n'existe qu'à partir du 1er message.
  // Déjà faite si un document a été déposé, qui l'exigeait avant l'envoi.
  if (!(await ensureConversation())) return;

  draft.value = '';
  clearAttachment();
  messages.value.push({
    key: nextKey(),
    role: 'user',
    content,
    attachment: jointe ? { name: jointe.name, truncated: jointe.truncated } : null,
  });

  // `reactive` et non un objet nu : poussé tel quel dans `messages`, le tableau
  // n'en garde que la version brute, et le rendu s'abonne au proxy qu'il relit.
  // Écrire dans la référence d'origine ne notifiait donc personne — la réponse
  // n'apparaissait qu'à la toute fin, quand `streaming` repassait à faux, et
  // tout le streaming ne se voyait pas. Le proxy et la valeur relue depuis
  // `messages` désignent le même objet : muter l'un redessine.
  const reply = reactive({
    key: nextKey(),
    role: 'assistant',
    content: '',
    // Le modèle est désigné par le serveur, qui le renvoie à la fin : rien à
    // afficher d'avance au pied de la réponse.
    model: null,
    pending: true,
    failed: false,
  });
  messages.value.push(reply);
  // Envoyer, c'est vouloir voir la réponse : on redescend même si le lecteur
  // était remonté dans l'historique du fil.
  scrollToBottom(true);

  streaming.value = true;
  abortController = new AbortController();

  smoothStream.start({
    write: (texte) => {
      // Mesuré avant l'écriture : à cet instant la mise en page est à jour,
      // la lecture ne coûte rien et dit vraiment où en est le lecteur.
      stickToBottom = isNearBottom();
      reply.content += texte;
    },
    afterWrite: () => scrollToBottom(),
  });

  await streamMessage(
    currentId.value,
    { content, attachment_id: jointe?.attachment_id },
    {
      onDelta: (fragment) => smoothStream.push(fragment),
      // `pending` n'est pas baissé ici : le tampon a encore du texte à
      // écrire, et le curseur de frappe doit clignoter jusqu'au dernier
      // caractère, pas jusqu'au dernier octet reçu.
      onDone: (payload) => {
        // Sans cet identifiant, le bouton « Télécharger » n'apparaissait qu'à
        // la réouverture de la conversation : le serveur le donne ici.
        reply.id = payload.message_id ?? reply.id;
        reply.model = payload.model || reply.model;
        reply.duration_ms = payload.duration_ms;
        syncConversationSummary(payload);
      },
      onError: (message) => {
        // Ce qui est déjà arrivé est du vrai texte : on l'affiche avant de
        // signaler l'échec, plutôt que de le perdre.
        smoothStream.flush();
        reply.pending = false;
        reply.failed = true;
        reply.error = message;
      },
    },
    abortController.signal,
  );

  // Le serveur a fini d'émettre ; le tampon solde ce qu'il lui reste au débit
  // de fin, sans quoi la queue de la réponse apparaîtrait d'un bloc.
  await smoothStream.finish();

  reply.pending = false;
  streaming.value = false;
  abortController = null;
  scrollToBottom();
}

/** Remonte la conversation en tête de liste et récupère son titre auto. */
function syncConversationSummary(payload) {
  const index = conversations.value.findIndex((c) => c.id === currentId.value);

  if (index === -1) return;

  const conversation = conversations.value[index];
  conversation.title = payload.title || conversation.title;
  conversation.last_message_at = new Date().toISOString();

  conversations.value.splice(index, 1);
  conversations.value.unshift(conversation);
}

/**
 * Interrompt l'affichage. La génération se poursuit côté serveur et la réponse
 * complète sera là à la réouverture de la conversation.
 */
function stopGeneration() {
  if (abortController) abortController.abort();
  // Demander l'arrêt, c'est vouloir la main tout de suite : le texte déjà reçu
  // s'affiche d'un coup au lieu de continuer à s'écrire lettre à lettre, et le
  // tampon se ferme pour qu'un dernier fragment en vol ne le relance pas.
  smoothStream.flush();
  smoothStream.stop();
  streaming.value = false;
}

/**
 * Ramène le fil en bas.
 *
 * Par défaut seulement si le lecteur y était déjà : pendant une réponse longue,
 * il peut remonter relire, et le texte qui s'écrit ne doit pas le ramener de
 * force. `force` sert aux gestes où descendre est justement ce qu'il demande —
 * envoyer un message, ouvrir une conversation.
 */
function scrollToBottom(force = false) {
  if (force) stickToBottom = true;
  if (!stickToBottom) return;

  nextTick(() => {
    const el = threadRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

/** Le lecteur est-il assez près du bas pour qu'on continue à l'y suivre ? */
function isNearBottom() {
  const el = threadRef.value;
  if (!el) return true;

  return el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_THRESHOLD_PX;
}

function labelForModel(id) {
  return models.value.find((m) => m.id === id)?.label || id;
}

function formatDuration(ms) {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/** Ce que Luce lira réellement du document, en clair. */
function formatChars(chars) {
  return chars >= 1000
    ? `${(chars / 1000).toFixed(1).replace('.0', '')} k caractères`
    : `${chars} caractères`;
}

function formatSize(bytes) {
  return bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} Mo`
    : `${Math.max(1, Math.round(bytes / 1024))} Ko`;
}

function formatDate(value) {
  if (!value) return '';

  const date = new Date(value);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();

  return sameDay
    ? date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
</script>

<style scoped>
/* Hauteur du chat : il remplit celle que son parent lui donne — la page dédiée
   comme le panneau flottant s'en chargent — et la répartit en flux flex. Aucun
   calcul en vh ici : une soustraction fixe laissait une bande morte sous la
   fenêtre dès que l'en-tête de page ne faisait pas la taille supposée. */
.ai-chat {
  --ai-sidebar-width: 270px;
  /* Plancher en pixels : si le parent ne transmet aucune hauteur, la fenêtre
     reste utilisable au lieu de s'écraser sur la hauteur de son contenu.
     Constaté sous Safari, où la conversation se réduisait à une bande. */
  --ai-min-height: 340px;

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: var(--ai-min-height);
}

.ai-chat.is-compact {
  --ai-sidebar-width: 200px;
  --ai-min-height: 300px;
}

/* Adossé au bord de l'écran, le chat occupe toute la hauteur disponible. La
   colonne d'historique ne tient pas à côté du fil dans 440 px : les deux
   passent en onglets et occupent tour à tour toute la largeur. */
.ai-chat.is-docked {
  --ai-min-height: 0;
}

.ai-chat.is-docked .ai-layout {
  grid-template-columns: 1fr;
}

.ai-chat.is-docked .ai-toolbar {
  flex: none;
  flex-wrap: nowrap;
  gap: 8px;
  margin-bottom: 8px;
}

.ai-chat.is-docked .ai-toolbar :deep(.el-button) {
  flex: none;
}

.ai-docked-tabs {
  flex: none;
}

/* Les volets d'onglet sont vides : seuls leurs en-têtes servent. */
.ai-docked-tabs :deep(.el-tabs__content) {
  display: none;
}

.ai-docked-tabs :deep(.el-tabs__header) {
  margin: 0 0 10px;
}

.ai-chat.is-docked .ai-sidebar,
.ai-chat.is-docked .ai-main {
  grid-column: 1 / -1;
}

.ai-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 12px;
}

/* Les deux cartes s'étirent par le grid, mais leur corps réclamait une hauteur
   en pourcentage — qui s'effondre dès que la chaîne de hauteurs se rompt. C'est
   le flux flex qui la donne, pas un calcul. */
.ai-main,
.ai-sidebar {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ai-layout {
  display: grid;
  grid-template-columns: var(--ai-sidebar-width) 1fr;
  gap: 14px;
  align-items: stretch;
  /* Toute la hauteur laissée par la barre d'outils, et pas un pixel de plus :
     le fil et l'historique défilent, la fenêtre ne pousse pas. */
  flex: 1 1 auto;
  min-height: 0;
}

@media (max-width: 900px) {
  .ai-layout {
    grid-template-columns: 1fr;
  }
}

.ai-sidebar :deep(.ai-sidebar-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding: 8px;
}

/* C'est le volet lui-même qui borne le défilement de la liste : elle occupe la
   hauteur de la carte, ni plus ni moins. */
.ai-conversation-list {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

.ai-conversation-item {
  position: relative;
  display: block;
  width: 100%;
  text-align: left;
  padding: 9px 30px 9px 11px;
  margin-bottom: 3px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font: inherit;
}

.ai-conversation-item:hover {
  background: var(--el-fill-color-light);
}

.ai-conversation-item.is-active {
  background: var(--el-color-primary-light-9);
}

.ai-conversation-title {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--el-text-color-primary);
}

.ai-conversation-meta {
  display: block;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.ai-conversation-delete {
  position: absolute;
  top: 9px;
  right: 8px;
  display: none;
  color: var(--el-text-color-secondary);
}

.ai-conversation-item:hover .ai-conversation-delete {
  display: inline-flex;
}

.ai-conversation-delete:hover {
  color: var(--el-color-danger);
}

.ai-main :deep(.ai-main-body) {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
}

.ai-thread {
  flex: 1 1 auto;
  /* Sans ce zéro, un élément flex refuse de descendre sous la hauteur de son
     contenu : le fil pousse le composeur dehors, ou s'effondre selon le moteur. */
  min-height: 0;
  overflow-y: auto;
  padding: 18px 20px;
}

.ai-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--el-text-color-secondary);
  text-align: center;
}

/* L'accueil se lit debout devant l'écran : on borne la largeur pour garder des
   lignes courtes plutôt que d'étaler le texte sur toute la fenêtre. */
.ai-placeholder p {
  max-width: 34rem;
  margin: 0;
}

.ai-placeholder-titre {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.ai-placeholder-reserve {
  font-size: 0.86em;
  opacity: 0.8;
}

.ai-message {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
}

.ai-message.is-user {
  align-items: flex-end;
}

.ai-message-bubble {
  max-width: 82%;
  padding: 11px 14px;
  border-radius: 10px;
  background: var(--el-fill-color-light);
}

.ai-message.is-user .ai-message-bubble {
  background: var(--el-color-primary-light-9);
}

.ai-message-error {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 7px;
  font-size: 13px;
  color: var(--el-color-danger);
}

.ai-message-download {
  margin-right: 8px;
  cursor: pointer;
}

.ai-download-trigger {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--el-color-primary);
}

.ai-message-footer {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.ai-composer {
  /* Jamais comprimé : quand la saisie s'allonge, c'est le fil qui cède la
     place, pas la zone de saisie qui se fait rogner. */
  flex: none;
  border-top: 1px solid var(--el-border-color-lighter);
  padding: 12px 14px;
  /* Le liseré du survol de dépôt se dessine à l'intérieur : une bordure
     ajoutée décalerait toute la zone de saisie au passage du fichier. */
  outline-offset: -3px;
}

.ai-composer.is-dropping {
  outline: 2px dashed var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.ai-composer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 9px;
}

/* L'input natif reste dans le flux pour rester atteignable au clavier via le
   bouton qui le déclenche, mais n'a pas à être vu. */
.ai-file-input {
  display: none;
}

.ai-attachment {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  padding: 5px 9px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: var(--el-fill-color-light);
  font-size: 12px;
}

.ai-attachment-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-attachment-meta {
  color: var(--el-text-color-secondary);
}

.ai-message-attachment {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  font-size: 12px;
  opacity: 0.85;
}
</style>
